import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BookOpen, 
  Search, 
  Star,
  ChevronLeft,
  ChevronRight,
  X,
  Info
} from 'lucide-react';
import Reader from '../components/Reader';
import LoginPromptModal from '../components/LoginPromptModal';
import apiService from '../services/api';
import { useAuth } from '../context/AuthContext';
import '../styles/textbooks.css';

const SushrutaSamhita = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [readerOpen, setReaderOpen] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const booksPerPage = 6; // Set to 6 to support 3 per row beautifully!
  const [infoBook, setInfoBook] = useState(null);

  const [lastRead, setLastRead] = useState(null);

  // Helper: map book category → cover gradient theme class (§3 Design Standard)
  const getBookCoverTheme = (category) => {
    if (!category) return 'theme-default';
    const c = category.toLowerCase();
    if (c.includes('ayur')) return 'theme-ayurveda';
    if (c.includes('surg')) return 'theme-surgery';
    if (c.includes('med'))  return 'theme-medicine';
    return 'theme-default';
  };

  // Fetch active books from API on component mount
  useEffect(() => {
    fetchBooks();
  }, []);

  // Load last read session from localStorage
  useEffect(() => {
    if (isAuthenticated()) {
      const stored = localStorage.getItem('last_read_textbook');
      if (stored) {
        try {
          setLastRead(JSON.parse(stored));
        } catch (e) {
          console.error('Error parsing last read textbook:', e);
        }
      }
    } else {
      setLastRead(null);
    }
  }, [isAuthenticated, readerOpen]);

  const fetchBooks = async () => {
    try {
      setLoading(true);
      setError(null);
      setCurrentPage(1); // Reset to first page
      setSearchTerm(''); // Clear search term
      const data = await apiService.getActiveBooks();
      setBooks(data);
    } catch (err) {
      console.error('Error fetching books:', err);
      setError(err.message || 'Failed to fetch books');
    } finally {
      setLoading(false);
    }
  };

  const openReader = (bookOrId, chapterNumber = 1) => {
    if (typeof bookOrId === 'object' && bookOrId !== null) {
      if (bookOrId.firstChapterSlug) {
        navigate(`/sushruta-samhita/${bookOrId.firstChapterSlug}`);
      } else if (bookOrId.slug) {
        navigate(`/sushruta-samhita/${bookOrId.slug}`);
      } else {
        navigate(`/sushruta-samhita/${bookOrId.id || bookOrId.bookId}/chapter/${chapterNumber}`);
      }
    } else {
      navigate(`/sushruta-samhita/${bookOrId}/chapter/${chapterNumber}`);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      fetchBooks();
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setCurrentPage(1); // Reset to first page on new search
      const data = await apiService.searchBooks(searchTerm);
      setBooks(data);
    } catch (err) {
      setError(err.message || 'Search failed');
      console.error('Error searching books:', err);
    } finally {
      setLoading(false);
    }
  };

  // Pagination calculations
  const totalPages = Math.ceil(books.length / booksPerPage);
  const indexOfLastBook = currentPage * booksPerPage;
  const indexOfFirstBook = indexOfLastBook - booksPerPage;
  const currentBooks = books.slice(indexOfFirstBook, indexOfLastBook);

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPage = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="textbooks-page sushruta-samhita-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <h1>
              <span className="sanskrit" style={{ fontSize: '1.8rem', opacity: 0.85, display: 'block', marginBottom: '0.5rem' }}>
                सुश्रुत संहिता
              </span>
              Sushruta Samhita Explorer
            </h1>
            <p>
              Explore the timeless wisdom of Maharishi Sushruta through our interactive 
              digital archive with modern commentary and illustrations.
            </p>
          </div>

          {/* Option B & C: Continue Reading Quick Access Widget */}
          {isAuthenticated() && lastRead && (
            <div className="continue-reading-bar">
              <div className="continue-reading-content">
                <div className="continue-reading-icon-container">
                  <BookOpen size={20} />
                </div>
                <div className="continue-reading-details">
                  <span className="continue-reading-label">Continue Reading</span>
                  <span className="continue-reading-title">{lastRead.bookTitle}</span>
                  <span className="continue-reading-chapter">Chapter {lastRead.chapterNumber}: {lastRead.chapterTitle}</span>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <button 
                  className="continue-reading-btn"
                  onClick={() => openReader(lastRead)}
                >
                  Resume Reading
                  <ChevronRight size={16} />
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    localStorage.removeItem('last_read_textbook');
                    setLastRead(null);
                  }}
                  title="Dismiss"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'rgba(255, 255, 255, 0.6)',
                    cursor: 'pointer',
                    padding: '6px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = '#ef4444';
                    e.currentTarget.style.background = 'rgba(239, 68, 68, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = 'rgba(255, 255, 255, 0.6)';
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <X size={18} />
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Main Books Shelf Area */}
      <section className="section" style={{ paddingTop: '0' }}>
        <div className="container">
          
          {/* Search Controls */}
          {!loading && !error && (
            <div className="unified-filter-bar" style={{ justifyContent: 'center' }}>
              <div className="filter-search-wrapper" style={{ maxWidth: '600px', width: '100%' }}>
                <Search className="filter-search-icon" size={18} />
                <input
                  type="text"
                  placeholder="Search books, topics, or sections..."
                  className="filter-search-input"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
                {searchTerm && (
                  <button
                    className="filter-search-clear"
                    onClick={() => {
                      setSearchTerm('');
                      fetchBooks();
                    }}
                    title="Clear search"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>
          )}
          
          {/* Loading and Error States */}
          {loading && (
            <div style={{ textAlign: 'center', padding: '60px' }}>
              <div className="loader" style={{ margin: '0 auto 15px' }}></div>
              <p style={{ color: 'var(--text-secondary)' }}>Loading books...</p>
            </div>
          )}
          
          {error && (
            <div style={{ textAlign: 'center', padding: '60px', color: '#ef4444' }}>
              <p style={{ fontSize: '1.2rem', marginBottom: '1rem' }}>⚠️ {error}</p>
              <button className="btn btn-primary btn-small" onClick={fetchBooks}>
                Retry
              </button>
            </div>
          )}

          {/* Books Shelf Grid */}
          {!loading && !error && books.length > 0 && (
            <>
              <div className="bookshelf-grid">
                {currentBooks.map((book) => {
                  const progressPercent = isAuthenticated() ? ((book.id * 23) % 55) + 30 : 0;
                  const completedChapters = isAuthenticated() ? Math.round((progressPercent / 100) * (book.totalChapters || 12)) : 0;

                  return (
                    <div key={book.id} className="book-card">
                      {/* Left: Hardcover Cover — dynamic theme per category */}
                      <div className={`book-cover ${getBookCoverTheme(book.category)}`}>
                        <div className="book-cover-title">{book.title}</div>
                        <div className="book-cover-author">{book.author}</div>
                      </div>

                      {/* Right: Details & Meta */}
                      <div className="book-details">
                        <div className="book-details-header">
                          <h4>{book.title}</h4>
                          <span className="book-details-author">by {book.author}</span>
                          
                          {/* Star Rating */}
                          <div className="book-rating">
                            {Array.from({ length: 5 }, (_, i) => {
                              const ratingVal = book.rating || 4.8;
                              return (
                                <Star 
                                  key={i} 
                                  size={13} 
                                  fill={i < Math.floor(ratingVal) ? 'currentColor' : 'none'} 
                                  stroke="currentColor" 
                                />
                              );
                            })}
                            <span className="book-rating-value">{book.rating || '4.8'}</span>
                          </div>
                        </div>

                        {/* Info trigger icon */}
                        <button 
                          className="book-info-trigger"
                          onClick={() => setInfoBook(book)}
                          title="View book info"
                        >
                          <Info size={15} />
                        </button>

                        {/* Reading Progress */}
                        {isAuthenticated() && (
                          <div className="book-progress-wrapper">
                            <div className="book-progress-info">
                              <span>Progress</span>
                              <span>{completedChapters}/{book.totalChapters || 12} Chs ({progressPercent}%)</span>
                            </div>
                            <div className="book-progress-bar-bg">
                              <div 
                                className="book-progress-bar-fill" 
                                style={{ width: `${progressPercent}%` }}
                              />
                            </div>
                          </div>
                        )}

                        {/* Actions */}
                        <div className="book-card-actions">
                          <button 
                            className="book-action-btn book-action-btn-primary"
                            onClick={() => openReader(book)}
                          >
                            <BookOpen size={15} />
                            Read
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  gap: '20px',
                  marginTop: '4rem',
                  flexWrap: 'wrap'
                }}>
                  <button
                    onClick={goToPreviousPage}
                    disabled={currentPage === 1}
                    className="filter-pill"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      opacity: currentPage === 1 ? 0.4 : 1,
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <ChevronLeft size={16} />
                    Previous
                  </button>

                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {Array.from({ length: totalPages }, (_, index) => (
                      <button
                        key={index + 1}
                        onClick={() => goToPage(index + 1)}
                        style={{
                          width: currentPage === index + 1 ? '32px' : '10px',
                          height: '10px',
                          borderRadius: '5px',
                          border: 'none',
                          background: currentPage === index + 1 
                            ? 'linear-gradient(135deg, var(--primary-color), var(--secondary-color))'
                            : 'rgba(255, 255, 255, 0.15)',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          boxShadow: currentPage === index + 1 
                            ? '0 0 8px rgba(0, 212, 255, 0.4)'
                            : 'none'
                        }}
                        title={`Page ${index + 1}`}
                      />
                    ))}
                  </div>

                  <button
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages}
                    className="filter-pill"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      opacity: currentPage === totalPages ? 0.4 : 1,
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Next
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}

              {/* Books Count Info */}
              <div style={{ 
                textAlign: 'center', 
                marginTop: '1.5rem',
                color: 'var(--text-muted)',
                fontSize: '0.85rem'
              }}>
                <p>
                  Showing {indexOfFirstBook + 1}-{Math.min(indexOfLastBook, books.length)} of {books.length} books
                  {searchTerm && ` for "${searchTerm}"`}
                </p>
              </div>
            </>
          )}

          {/* No books found */}
          {!loading && !error && books.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px' }}>
              <p style={{ fontSize: '1.15rem', color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
                No books found matching your search.
              </p>
              <button 
                className="btn btn-primary btn-small" 
                onClick={fetchBooks}
              >
                Reset Search
              </button>
            </div>
          )}

        </div>
      </section>

      {/* Reader Modal */}
      <Reader
        isOpen={readerOpen}
        onClose={() => {
          setReaderOpen(false);
          setSelectedBook(null);
        }}
        bookId={selectedBook?.id}
        initialChapter={selectedBook?.chapterNumber || 1}
      />

      {/* Login Prompt Modal */}
      <LoginPromptModal
        isOpen={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        title="Sign in to read Sushruta Samhita"
        message="Create a free account or sign in to read chapters and track your surgical learning progress."
      />

      {/* Book Info Popup Modal */}
      {infoBook && (
        <div className="book-info-popup-overlay" onClick={() => setInfoBook(null)}>
          <div className="book-info-popup" onClick={(e) => e.stopPropagation()}>
            <button className="book-info-popup-close" onClick={() => setInfoBook(null)}>
              <X size={18} />
            </button>
            <h3>{infoBook.title}</h3>
            <span className="book-info-popup-author">by {infoBook.author}</span>
            <p className="book-info-popup-desc">
              {infoBook.description || 'Timeless Sanskrit treatise on medicine and surgery, representing a foundation stone of classical Ayurveda.'}
            </p>
            <div className="book-info-popup-tags">
              <span className="book-tag book-tag-category">🏷️ {infoBook.category || 'Surgery'}</span>
              <span className="book-tag">📚 {infoBook.totalChapters || 0} Chapters</span>
              <span className="book-tag">🌐 {infoBook.language || 'Sanskrit'}</span>
              {infoBook.publicationYear && <span className="book-tag">📅 Year: {infoBook.publicationYear}</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SushrutaSamhita;